import { useMemo } from "react";
import { ApolloClient, Reference, HttpLink, InMemoryCache, NormalizedCacheObject, ApolloLink, from } from "@apollo/client";
import { concatPagination } from "@apollo/client/utilities";
import { IncomingHttpHeaders } from "http";
import { storage } from "@utils/storage";
import { AppProps } from "next/app";
import merge from "deepmerge";
import isEqual from "lodash/isEqual";

export const APOLLO_STATE_PROP_NAME = "__APOLLO_STATE__";
interface IInitialState {
	initialState: NormalizedCacheObject | null;
}

let apolloClient: ApolloClient<NormalizedCacheObject> | null = null;
let apolloAuthClient: ApolloClient<NormalizedCacheObject> | null = null;

function createApolloClient() {
	return new ApolloClient({
		ssrMode: typeof window === "undefined", // set to true for SSR
		link: new HttpLink({
			uri: process.env.NEXT_PUBLIC_WORDPRESS_GRAPHQL_URL,
		}),
		cache: new InMemoryCache({
			typePolicies: {
				Query: {
					fields: {
						posts: {
							keyArgs: false,
							merge(existing, incoming) {
								let posts: Reference[] = [];
								if (existing && existing.posts) {
									posts = posts.concat(existing.posts);
								}
								if (incoming && incoming.posts) {
									posts = posts.concat(incoming.posts);
								}
								return {
									...incoming,
									posts,
								};
							},
						},
					},
				},
			},
		}),
	});
}

export function initializeApollo(initialState: IInitialState = null): ApolloClient<NormalizedCacheObject> {
	const _apolloClient = apolloClient ?? createApolloClient();

	// If your page has Next.js data fetching methods that use Apollo Client,
	// the initial state gets hydrated here
	if (initialState) {
		// Get existing cache, loaded during client side data fetching
		const existingCache = _apolloClient.extract();

		// Restore the cache using the data passed from
		// getStaticProps/getServerSideProps combined with the existing cached data
		_apolloClient.cache.restore({ ...existingCache, ...initialState });
	}

	// For SSG and SSR always create a new Apollo Client
	if (typeof window === "undefined") return _apolloClient;

	// Create the Apollo Client once in the client
	if (!apolloClient) apolloClient = _apolloClient;
	return _apolloClient;
}

export function useApollo(initialState: IInitialState): ApolloClient<NormalizedCacheObject> {
	const store = useMemo(() => initializeApollo(initialState), [initialState]);
	return store;
}

function createAuthApolloClient(headers: IncomingHttpHeaders) {
	return new ApolloClient({
		ssrMode: typeof window === "undefined",
		name: typeof window === "undefined" ? "amigoedu-site-next-ssr" : "amigoedu-site-next-nossr",
		link: from([timeStartLink, logTimeLink, terminatingLink(headers)]),
		cache: new InMemoryCache({
			typePolicies: {
				Query: {
					fields: {
						allPosts: concatPagination(),
					},
				},
			},
		}),
	});
}

export function initializeAuthApollo(initialState = null, headers = {}): ApolloClient<NormalizedCacheObject> {
	const _apolloAuthClient = createAuthApolloClient(headers);

	// If your page has Next.js data fetching methods that use Apollo Client, the initial state
	// gets hydrated here
	if (initialState) {
		// Get existing cache, loaded during client side data fetching
		const existingCache = _apolloAuthClient.extract();

		// Merge the existing cache into data passed from getStaticProps/getServerSideProps
		const data = merge(initialState, existingCache, {
			// combine arrays using object equality (like in sets)
			arrayMerge: (destinationArray, sourceArray) => [
				...sourceArray,
				...destinationArray.filter((d) => sourceArray.every((s) => !isEqual(d, s))),
			],
		});

		// Restore the cache with the merged data
		_apolloAuthClient.cache.restore(data);
	}
	// For SSG and SSR always create a new Apollo Client
	if (typeof window === "undefined") return _apolloAuthClient;
	// Create the Apollo Client once in the client
	if (!apolloAuthClient) apolloAuthClient = _apolloAuthClient;

	return _apolloAuthClient;
}

export function useAuthApollo(pageProps: AppProps["pageProps"]): ApolloClient<NormalizedCacheObject> {
	const state = pageProps[APOLLO_STATE_PROP_NAME];
	const store = useMemo(() => initializeAuthApollo(state, pageProps), [state]);
	return store;
}

const timeStartLink = new ApolloLink((operation, forward) => {
	operation.setContext({ start: new Date() });
	return forward(operation);
});

const logTimeLink = new ApolloLink((operation, forward) => {
	return forward(operation).map((data) => {
		// data from a previous link
		const time = new Date().getTime() - operation.getContext().start.getTime();
		console.log(
			`[${typeof window === "undefined" ? "ssr" : "nossr"}] operation ${operation.operationName} took ${time} to complete
			${JSON.stringify(operation.variables)}`,
		);
		return data;
	});
});

export const getTokenFromLS = () => (storage.getItem("user") !== null ? JSON.parse(storage.getItem("user")!).token : "");

const terminatingLink = (pageProps) => {
	const token = typeof window === "undefined" ? pageProps?.token : getTokenFromLS();

	return new HttpLink({
		uri: typeof window === "undefined" ? process.env.API_URL_SSR_MODE : process.env.NEXT_PUBLIC_API_URL,
		headers: {
			authorization: token,
			userId: pageProps?.userId,
		},
	});
};
